import classNames from "classnames";
import useElementOnScreen from "../../../hooks/useElementOnScreen";
import flyORKA from "../../imagenes/Logo_Tables.png";
import "./HighlightedTable.scss";

type HighlightedTableMap<T extends string> = Record<T, string>;

type HighlightedTableColumn<T extends string> = {
	title: string;
	columns: HighlightedTableMap<T>;
};

export type HighlightedTableContent<T extends string> = {
	enabled: boolean;
	title: string;
	content?: string;
	columnsHeaders: readonly T[];
	otherApps?: HighlightedTableColumn<T>;
	flyORKA: HighlightedTableColumn<T>;
};

const HighlightedTable = <T extends string>({
	columnsHeaders,
	columns,
	className,
	isMobile,
}: {
	columnsHeaders: readonly T[];
	columns: HighlightedTableColumn<T>[];
	className?: string;
	isMobile?: boolean;
}) => {
	const [containerRef, isVisible] = useElementOnScreen<Element>();
	const hasFadeInFromDirection = /fade-in--from-[^\s]+/.test(className || "");

	const TableHeaders = () => {
		return (
			<thead>
				<tr>
					{(columns.length > 1 || (!isMobile && columns.length === 1)) && (
						<th className="table__cell table__header table__header--empty"></th>
					)}
					{columns?.map((column) => (
						<th
							key={column.title}
							colSpan={columns.length <= 1 ? 2 : undefined}
							className={classNames(
								"table__cell table__header table__header--top",
								{
									"table__cell--recommended": column.title === "FlyORKA",
								},
							)}>
							{column.title === "FlyORKA" ? (
								<img
									src={flyORKA}
									className="table__header--img"
									alt={column.title}
								/>
							) : (
								column.title
							)}
						</th>
					))}
				</tr>
			</thead>
		);
	};

	const tableData = columnsHeaders.map((header) => {
		const columnName = header.replace("-", " ");
		return (
			<tr key={header}>
				<th className="table__cell table__header table__header--left">
					{columnName}
				</th>
				{columns?.map((column) => (
					<td
						key={column.title}
						className={classNames("table__cell table__data", {
							"table__cell--recommended": column.title === "FlyORKA",
						})}>
						{column.columns[header]}
					</td>
				))}
			</tr>
		);
	});

	return (
		<table
			className={classNames(
				"table fade-in fade-in--from-left",
				{
					"table__all-columns":
						columns.length > 1 || (!isMobile && columns.length === 1),
					"table__has-single-data-column": columns.length === 1,
				},
				className,
				{
					"fade-in--from-left": !hasFadeInFromDirection,
					visible: isVisible,
				},
			)}
			ref={containerRef as any}>
			<TableHeaders />
			<tbody>{tableData}</tbody>
		</table>
	);
};

export default HighlightedTable;
